import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { trackAddToCart, trackRemoveFromCart } from '../hooks/useGoogleAnalytics';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      setCart({ items: [], total: 0, item_count: 0 });
      // Cart fetch failed - reset to empty cart silently (auth context handles token issues)
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    } else {
      setCart({ items: [], total: 0, item_count: 0 });
    }
  }, [isAuthenticated, fetchCart]);

  const addToCart = async (productId, quantity = 1, productData = null) => {
    if (!token) return;
    try {
      const response = await axios.post(
        `${API}/cart/add`,
        { product_id: productId, quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart(response.data);
      
      // Track GA4 add_to_cart event
      // Find the added item in the response to get full product info
      const addedItem = response.data.items?.find(item => 
        item.product_id === productId || item.id === productId
      );
      if (addedItem || productData) {
        trackAddToCart(addedItem || productData, quantity);
      }
      
      return response.data;
    } catch (error) {
      // Re-throw for caller to handle
      throw error;
    }
  };

  const updateCartItem = async (productId, quantity) => {
    if (!token) return;
    try {
      const response = await axios.put(
        `${API}/cart/update`,
        { product_id: productId, quantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart(response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const removeFromCart = async (productId, productData = null) => {
    if (!token) return;
    
    // Find the item being removed before the API call for GA4 tracking
    const removedItem = cart.items?.find(item => 
      item.product_id === productId || item.id === productId
    );
    
    try {
      const response = await axios.delete(
        `${API}/cart/remove/${productId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart(response.data);
      
      // Track GA4 remove_from_cart event
      if (removedItem || productData) {
        trackRemoveFromCart(removedItem || productData, removedItem?.quantity || 1);
      }
      
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const clearCart = async () => {
    if (!token) return;
    try {
      const response = await axios.delete(
        `${API}/cart/clear`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCart(response.data);
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const value = {
    cart,
    loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    refreshCart: fetchCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
